import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Outlet } from "@tanstack/react-router";
import { createContext, useEffect, useState } from "react";
import { ConfigData } from "@/api/config";
import TestIndicator from "@/components/TestIndicator/TestIndicator";
import { Order } from "./api/verify";
import { ReturnOrder } from "./types";
import {
  decodeConfig,
  getConfigFileName,
  setDocumentTitle,
  setFavicon,
} from "./utils/utils";

export const ConfigContext = createContext<ConfigData | undefined>(undefined);

export const ReturnOrderContext = createContext<{
  returnOrder: ReturnOrder | undefined;
  setReturnOrder: (returnOrder: ReturnOrder | undefined) => void;
}>({ returnOrder: undefined, setReturnOrder: () => {} });

export const OrderDataContext = createContext<{
  orderData: Order | undefined;
  setOrderData: (orderData: Order | undefined) => void;
}>({ orderData: undefined, setOrderData: () => {} });

const CONFIG_FETCH_TIMEOUT_MS = 10000;

const fetchConfigJson = async (url: string): Promise<ConfigData> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    CONFIG_FETCH_TIMEOUT_MS
  );

  try {
    const response = await fetch(url, { signal: controller.signal });

    if (!response.ok) {
      const body = await response.text().catch(() => "");
      const bodyPreview = body ? `: ${body.slice(0, 200)}` : "";

      throw new Error(
        `Error fetching config from ${url}: ${response.status} ${response.statusText}${bodyPreview}`
      );
    }

    try {
      return (await response.json()) as ConfigData;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new Error(`Error parsing config from ${url}: ${message}`);
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error(
        `Timed out fetching config from ${url} after ${CONFIG_FETCH_TIMEOUT_MS}ms`
      );
    }

    throw err;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export default function Layout() {
  const [configData, setConfigData] = useState<ConfigData | undefined>(
    undefined
  );
  const [returnOrder, setReturnOrder] = useState<ReturnOrder | undefined>(
    undefined
  );
  const [orderData, setOrderData] = useState<Order | undefined>(undefined);
  const [testMode, setTestMode] = useState<boolean>(false);

  const fetchConfig = async () => {
    let data: ConfigData;
    if (import.meta.env.MODE === "development") {
      // Read local json
      data = await fetchConfigJson("/dev-portal-config.json");
    } else {
      // Get config from API
      const CONFIG_URL = `${import.meta.env.VITE_CONFIG_URL}/${getConfigFileName()}`;
      data = await fetchConfigJson(CONFIG_URL);
    }
    // Override config if config query param is present
    const searchParams = new URLSearchParams(document.location.search);
    if (searchParams.has("config")) {
      try {
        const config = decodeConfig(
          searchParams.get("config")!
        ) as Partial<ConfigData>;
        if (Object.keys(config).length > 0)
          data = {
            ...data,
            ...config,
          };
      } catch (err) {
        console.error(err);
      }
    }
    return data;
  };

  useEffect(() => {
    let cancelled = false;

    fetchConfig()
      .then((config) => {
        if (!cancelled) setConfigData(config);
      })
      .catch((err) => {
        console.error(err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(document.location.search);
    if (searchParams.has("testMode"))
      setTestMode(searchParams.get("testMode") === "1");
  }, []);

  useEffect(() => {
    if (configData) {
      setFavicon(configData.favicon_src);
      setDocumentTitle(configData.document_title_text);
    }
  }, [configData]);

  return (
    <>
      <TestIndicator testMode={testMode} />
      <ReturnOrderContext.Provider value={{ returnOrder, setReturnOrder }}>
        <OrderDataContext.Provider value={{ orderData, setOrderData }}>
          <ConfigContext.Provider value={configData}>
            <div
              id="layout-container"
              className="w-full sm:h-screen bg-white flex justify-center items-center overflow-y-scroll"
              style={{
                backgroundImage: `url(${configData?.background_src})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Outlet />
            </div>
            <ReactQueryDevtools initialIsOpen={false} />
          </ConfigContext.Provider>
        </OrderDataContext.Provider>
      </ReturnOrderContext.Provider>
    </>
  );
}
